import React, { useCallback, useEffect, useRef, useState } from "react";

import { noticesService } from "api/services/notices.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Pagination from "components/UI/Pagination";
import RichTextEditor from "components/UI/RichTextEditor";
import Table, { Column } from "components/UI/Table";
import Toggle from "components/UI/Toggle";
import { isBlankNoticeContent, toNoticeEditorHtml } from "utils/notice-content";

import type { NoticeCreateRequest, NoticeDetail, NoticeListItem } from "api/types/notice.types";
import "pages/AdminPage.scss";
import "./Announcement.scss";

const PAGE_SIZE = 20;

interface FormState {
  title: string;
  content: string;
  is_important: boolean;
  published_at: string;
}

interface FormErrors {
  title?: string;
  content?: string;
  published_at?: string;
}

const EMPTY_FORM: FormState = {
  title: "",
  content: "",
  is_important: false,
  published_at: "",
};

function toDatetimeLocal(isoString: string): string {
  if (!isoString) return "";
  // Convert ISO 8601 → datetime-local input format "YYYY-MM-DDTHH:mm"
  return isoString.slice(0, 16);
}

function toISOString(datetimeLocal: string): string {
  if (!datetimeLocal) return "";
  return new Date(datetimeLocal).toISOString();
}

function formatDate(isoString: string): string {
  if (!isoString) return "-";
  return isoString.slice(0, 10);
}

const Announcement: React.FC = () => {
  const [items, setItems] = useState<NoticeListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // 등록/수정 모달
  const [formModal, setFormModal] = useState<{
    open: boolean;
    mode: "create" | "edit";
    detail: NoticeDetail | null;
  }>({ open: false, mode: "create", detail: null });
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [formDetailLoading, setFormDetailLoading] = useState(false);
  // 진행 중인 본문 이미지 업로드 수 (동시 업로드 대비 count)
  const [imageUploading, setImageUploading] = useState(0);
  const imageUploadingRef = useRef(0);

  // 삭제 모달
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    item: NoticeListItem | null;
  }>({ open: false, item: null });
  const [actionLoading, setActionLoading] = useState(false);

  const fetchList = useCallback(async (targetPage = 1) => {
    setLoading(true);
    try {
      const res = await noticesService.list({ page: targetPage, page_size: PAGE_SIZE });
      setItems(res.data.results ?? []);
      setTotalCount(res.data.count ?? 0);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList(page);
  }, [fetchList, page]);

  const handlePageChange = (p: number) => {
    setPage(p);
  };

  // ── 검색 (클라이언트 사이드) ──────────────────────────────
  const filteredItems = items.filter((item) => {
    if (!search) return true;
    return item.title.toLowerCase().includes(search.toLowerCase());
  });

  // ── 등록 모달 열기 ────────────────────────────────────────
  const handleOpenCreate = () => {
    setForm(EMPTY_FORM);
    setFormErrors({});
    setFormModal({ open: true, mode: "create", detail: null });
  };

  // ── 수정 모달 열기 (상세 API 호출) ───────────────────────
  const handleOpenEdit = async (item: NoticeListItem) => {
    setForm(EMPTY_FORM);
    setFormErrors({});
    setFormModal({ open: true, mode: "edit", detail: null });
    setFormDetailLoading(true);
    try {
      const res = await noticesService.get(item.id);
      const d = res.data;
      setFormModal({ open: true, mode: "edit", detail: d });
      setForm({
        title: d.title,
        content: toNoticeEditorHtml(d.content),
        is_important: d.is_important,
        published_at: toDatetimeLocal(d.published_at),
      });
    } catch {
      setFormModal({ open: false, mode: "edit", detail: null });
    } finally {
      setFormDetailLoading(false);
    }
  };

  const handleCloseForm = () => {
    setFormModal({ open: false, mode: "create", detail: null });
    setFormErrors({});
  };

  // ── 폼 유효성 검사 ────────────────────────────────────────
  const validate = (): boolean => {
    const errors: FormErrors = {};
    if (!form.title.trim()) errors.title = "제목을 입력해주세요.";
    if (isBlankNoticeContent(form.content)) errors.content = "내용을 입력해주세요.";
    if (!form.published_at) errors.published_at = "게시일을 입력해주세요.";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── 본문 에디터 ──────────────────────────────────────────
  const handleContentChange = useCallback((html: string) => {
    setForm((f) => ({ ...f, content: html }));
  }, []);

  // BE 에 공지 전용 업로드 엔드포인트가 없어 어드민 전용 배너 업로드 API 를 재사용한다.
  // RichTextEditor 는 uploadImage 참조가 바뀌면 에디터를 재생성하므로 useCallback 으로 고정.
  const uploadNoticeImage = useCallback(async (file: File): Promise<string> => {
    imageUploadingRef.current += 1;
    setImageUploading(imageUploadingRef.current);
    try {
      const { data } = await noticesService.uploadBannerImage(file);
      return data.url;
    } finally {
      imageUploadingRef.current -= 1;
      setImageUploading(imageUploadingRef.current);
    }
  }, []);

  // ── 저장 (등록/수정) ─────────────────────────────────────
  const handleSave = async () => {
    // 업로드가 끝나기 전에 저장하면 아직 본문에 삽입되지 않은 이미지가 누락된다
    if (imageUploading > 0 || !validate()) return;
    setActionLoading(true);
    const payload: NoticeCreateRequest = {
      title: form.title.trim(),
      content: form.content.trim(),
      is_important: form.is_important,
      published_at: toISOString(form.published_at),
    };
    try {
      if (formModal.mode === "create") {
        await noticesService.create(payload);
      } else if (formModal.detail) {
        await noticesService.update(formModal.detail.id, payload);
      }
      handleCloseForm();
      fetchList(page);
    } catch {
      // ignore
    } finally {
      setActionLoading(false);
    }
  };

  // ── 삭제 ─────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteModal.item) return;
    setActionLoading(true);
    try {
      await noticesService.remove(deleteModal.item.id);
      setDeleteModal({ open: false, item: null });
      fetchList(page);
    } catch {
      // ignore
    } finally {
      setActionLoading(false);
    }
  };

  // ── 테이블 컬럼 ──────────────────────────────────────────
  const columns: Column<NoticeListItem>[] = [
    {
      key: "index",
      title: "번호",
      width: "60px",
      render: (_v, _row, idx) => (page - 1) * PAGE_SIZE + idx + 1,
    },
    {
      key: "title",
      title: "제목",
      render: (_v, row) => (
        <div className="announcement-title-cell">
          {row.is_important && <Badge variant="rejected">중요</Badge>}
          <span className="announcement-title-text">{row.title}</span>
        </div>
      ),
    },
    {
      key: "published_at",
      title: "게시일",
      width: "120px",
      render: (v) => formatDate(String(v)),
    },
    {
      key: "actions",
      title: "관리",
      width: "140px",
      render: (_v, row) => (
        <div className="announcement-actions">
          <Button size="sm" variant="outline" onClick={() => handleOpenEdit(row)}>
            수정
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => setDeleteModal({ open: true, item: row })}
          >
            삭제
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader
        title="공지사항 관리"
        description="공지사항을 등록하고 관리합니다."
        actions={[
          <Button key="create" variant="primary" onClick={handleOpenCreate}>
            + 공지사항 등록
          </Button>,
        ]}
      />

      <div className="admin-page__card">
        <div className="admin-page__filter">
          <Input
            placeholder="제목 검색"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            prefix="🔍"
          />
        </div>

        {loading ? (
          <div className="admin-page__loading">불러오는 중...</div>
        ) : (
          <Table columns={columns} data={filteredItems} rowKey="id" />
        )}

        {!loading && totalCount > PAGE_SIZE && (
          <div className="announcement-pagination">
            <Pagination
              current={page}
              total={totalCount}
              pageSize={PAGE_SIZE}
              onChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* 등록 / 수정 모달 */}
      <Modal
        open={formModal.open}
        title={formModal.mode === "create" ? "공지사항 등록" : "공지사항 수정"}
        width="800px"
        onClose={handleCloseForm}
        closeOnOverlayClick={false}
        footer={
          <div className="modal-footer-actions">
            <Button variant="secondary" onClick={handleCloseForm} disabled={actionLoading}>
              취소
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={actionLoading || formDetailLoading || imageUploading > 0}
            >
              {actionLoading ? "저장 중..." : imageUploading > 0 ? "이미지 업로드 중..." : "저장"}
            </Button>
          </div>
        }
      >
        {formDetailLoading ? (
          <div className="announcement-form-loading">불러오는 중...</div>
        ) : (
          <div className="announcement-form">
            <div className="announcement-form__field">
              <Input
                label="제목 *"
                placeholder="제목을 입력해주세요"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                error={formErrors.title}
              />
            </div>

            <div className="announcement-form__field">
              <label className="input-label">내용 *</label>
              <div
                className={`announcement-form__editor${formErrors.content ? " announcement-form__editor--error" : ""}`}
              >
                <RichTextEditor
                  value={form.content}
                  onChange={handleContentChange}
                  uploadImage={uploadNoticeImage}
                  placeholder="내용을 입력해주세요"
                  height={360}
                />
              </div>
              {formErrors.content && <span className="input-error">{formErrors.content}</span>}
            </div>

            <div className="announcement-form__field">
              <Input
                label="게시일 *"
                type="datetime-local"
                value={form.published_at}
                onChange={(e) => setForm((f) => ({ ...f, published_at: e.target.value }))}
                error={formErrors.published_at}
              />
            </div>

            <div className="announcement-form__field announcement-form__field--row">
              <span className="input-label">중요 공지</span>
              <Toggle
                checked={form.is_important}
                onChange={(checked) => setForm((f) => ({ ...f, is_important: checked }))}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* 삭제 확인 모달 */}
      <Modal
        open={deleteModal.open}
        title="공지사항 삭제"
        width="440px"
        onClose={() => setDeleteModal({ open: false, item: null })}
        footer={
          <div className="modal-footer-actions">
            <Button
              variant="secondary"
              onClick={() => setDeleteModal({ open: false, item: null })}
              disabled={actionLoading}
            >
              취소
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={actionLoading}>
              {actionLoading ? "삭제 중..." : "삭제"}
            </Button>
          </div>
        }
      >
        <p className="confirm-text">
          이 공지사항을 삭제하시겠습니까?
          <br />
          <strong>{deleteModal.item?.title}</strong>
        </p>
      </Modal>
    </div>
  );
};

export default Announcement;
